'use strict';
const axios = require('axios');
const Throttled = require('throttled-async');
const fillSketch = require('../helpers/Standard/String/fill');

module.exports = class {
	/**
	 * Creates {@link https://www.npmjs.com/package/throttled-async|throttling wrapper} above {@link https://www.npmjs.com/package/axios|axios}
	 * @constructor axios-throttled
	 * @param {Object} config - configs object
	 * @param {Object|String} config.query - axios `query` config (can be an url string)
	 * @param {Function} [config.stay] - throttling `stay` function
	 * @param {Number} [config.logs] - throttling `logs` parameter
	 */
	constructor(config = {}) {
		let {
			stay,
			logs
		} = config;

		({
			query: this.query,
			placemarks: this.placemarks
		} = config);

		this._configThrottled = {
			stay,
			logs
		};

		try {
			this._requester = new Throttled(axios, this._configThrottled);
		} catch (error) {
			console.error('Error during initialization with', config);
			throw error;
		}

		Object.keys(this._configThrottled).forEach(function (key) {
			Object.defineProperty(this, key, {
				get: function () {
					return this._requester[key];
				},
				set: function (value) {
					this._requester[key] = value;
				}
			});
		}, this);
	}

	/**
	 * Return axios query result
	 * @param {any} config - additional config for query
	 * @return {Promise}
	 * @example
	 * const AxiosT = require('axios-throttled');;
	 *
	 * const config = {
	 *  query: 'https://google.com/'
	 * };
	 *
	 * const axiosT = new AxiosT(config);
	 * await axiosT.requestRaw({params: {q: 'cats'}}); // return query result
	 */
	requestRaw(config) {
		return this._requester.run(Object.assign({}, this.query, config));
	}

	/**
	 * Return `data` key from `axios` query result
	 * @param {any} config - additional config for query
	 * @return {Promise}
	 */
	request(config) {
		return this.requestRaw(config).then(({data} = {}) => data);
	}

	/**
	 * Fill url sketch with data
	 * @param {Object} params - placeholders values
	 * @param {Array.string} [placemarks] - array with placemarks
	 * @return instance of this class
	 * @example
	 * const AxiosT = require('axios-throttled');;
	 *
	 * const configs = {
	 *  expand1: {
	 *    query: 'https://{host}/{path}',
	 *  },
	 *  expand2: {
	 *    query: 'https://[[host]]/[[path]]'
	 *  },
	 *  expand3: {
	 *    query: 'https://[[host]]/[[path]]',
	 *    placemarks: ['[[', ']]'],
	 *  },
	 * };
	 *
	 * // Using default placemarks - `{` & `}`
	 * const axiosT1 = new AxiosT(configs.expand1);
	 * const mail1 = await axiosT1.expandUrl({domain: 'mail.google.com', path: 'mail'}).request();
	 *
	 * // Using custom placemarks
	 * const axiosT2 = new AxiosT(configs.expand2);
	 * const mail2 = await axiosT2.expandUrl({domain: 'mail.google.com', path: 'mail'}, ['[[', ']]']).request();
	 *
	 * // Using preseted custom placemarks
	 * const axiosT3 = new AxiosT(configs.expand3);
	 * const mail3 = await axiosT3.expandUrl({domain: 'mail.google.com', path: 'mail'}).request();
	 */
	expandUrl(params = {}, placemarks) {
		placemarks = placemarks ? placemarks : this.placemarks;
		const url = fillSketch(this.query.url, params, placemarks);
		const instance = Object.create(this);
		instance.query = Object.assign({}, this.query, {url});
		return instance;
	}

	/**
	 * @return {Object}
	 */
	get query() {
		return this._query;
	}

	set query(value) {
		this._query = value instanceof String || typeof value === 'string' ? {url: value} : value;
	}

	/**
	 * @return {Array.string}
	 */
	get placemarks() {
		return this._placemarks;
	}

	set placemarks(value) {
		this._placemarks = value instanceof Array && value.length === 2 ? value : [];
	}

	/**
	 * getters & setters for axios {@link https://www.npmjs.com/package/throttled-async|throttling wrapper} (stay, logs) can be used for additional configuration
	 * @name throttling
	 * @example
	 * const AxiosT = require('axios-throttled');;
	 *
	 * const config = {
	 *  query: 'https://yahoo.com/',
	 * };
	 *
	 * const axiosT = new AxiosT(config);
	 * await axiosT.request({params: {q: 'dogs'}}); // will return query result
	 * axiosT.stay = () => Date.now() > (new Date('3000/01/01')).getTime() // blocks requests until year 3000
	 * await axiosT.request({params: {q: 'cats'}}); // will not invoke any request and return `undefined`
	 */
};
